import { Box, Card, Grid, Typography } from '@mui/material'
import { message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import queryString from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import ExportExcel from '../../../../components/Export'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import PageUrls from '../../../../constants/page-urls'
import useAxios from '../../../../hooks/useAxios'
import { IProductDailyReport } from '../../../../types/report'
import SendItemsReportFilter, {
  IFilterMapped,
} from '../../components/SendItemsReportFilter'
import IndividualDisplayItemsTable from '../DailyOrdersReport/components/IndividualDisplayItemsTable'
import ProductListReportTable from './components/ProductListReportTable'

const Menus: any[] = []

const SendItemsReportPage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)

  const tableRef = useRef(null)

  const [searchFilter, setSearchFilter] = useState<IFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(1, 'days').startOf('day').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().endOf('day').format(),
    shippingProvider: queryStr.shippingProvider
      ? String(queryStr.shippingProvider)
      : '',
    trackingStatus: 'tracking',
  })
  const [filter, setFilter] = useState<IFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(30, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    shippingProvider: queryStr.shippingProvider
      ? String(queryStr.shippingProvider)
      : '',
    trackingStatus: 'tracking',
  })

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.Report.SendItems}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(filter)
  }, [])

  const { data, isLoading } = useAxios<IProductDailyReport>({
    method: 'GET',
    url: '/report/product-daily-reports',
    params: {
      ...searchFilter,
      products: searchFilter.productObjectIds,
      categories: searchFilter.categoryObjectIds,
      shippingProvider:
        searchFilter.shippingProvider === 'all'
          ? undefined
          : searchFilter.shippingProvider,
      isPickup: '1',
    },
  })

  const handleOnDownload = useCallback(async (date: Dayjs) => {
    if (tableRef.current) {
      const ws = XLSX.utils.table_to_book(tableRef.current)
      const excelBuffer = XLSX.write(ws, { bookType: 'xlsx', type: 'array' })
      const data = new Blob([excelBuffer], { type: 'application/octet-stream' })

      const url = window.URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      link.download = `deliveryOrderDailyReport.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
      link.remove()
      message.success('ดาวน์โหลดสำเร็จ')
    } else {
      message.error('ไม่พบข้อมูลสำหรับการดาวน์โหลด')
    }
  }, [])

  return (
    <ContentWrapper>
      <Helmet title="รายงานส่งของ" />
      <HeaderContent
        goBack
        title="รายงานส่งของ"
        routes={Menus}
        descriptions="รายงานส่งของ"
      />

      <Card sx={{ padding: 2, mb: 3 }}>
        <SendItemsReportFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          showSippingProvider={true}
          showDownload={false}
        />
        <ProductListReportTable isLoading={isLoading} data={data} />
      </Card>

      <Card sx={{ padding: 2 }}>
        <Grid
          container
          spacing={2}
          alignItems={'center'}
          mb={2}
          justifyContent={'space-between'}
        >
          <Grid item xs={12} lg={6}>
            <Typography variant="h5" mb={0} color="text.secondary">
              รายการรวมสินค้าทั้งหมด
            </Typography>
          </Grid>
          <Grid item xs={12} lg={2}>
            <Box textAlign={'right'}>
              <ExportExcel onDownload={handleOnDownload} filterDate={false} />
            </Box>
          </Grid>
        </Grid>
        <IndividualDisplayItemsTable data={data?.total} ref={tableRef} />
      </Card>
    </ContentWrapper>
  )
}

export default SendItemsReportPage
